import { Button, Stack, Typography } from '@mui/material';
import { APP_IMG } from 'assets';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from 'routers/routes';
import { useAppSelector } from 'store/hook';
import {
  BoxWrapper,
  ImageVector,
  LabelSubTitle,
  LabelTitle,
  StyledButtonWrapper,
} from './styles';

const dataHowItWorks = [
  {
    title: '1. Request',
    elementDescription: (
      <Typography fontSize={12} fontWeight={400} color={'#193F56'}>
        Share your babysitting preferences and requirements to help us match you
        with the ideal babysitter.
      </Typography>
    ),
    src: APP_IMG.request,
  },
  {
    title: '2. Start Searching',
    elementDescription: (
      <Typography fontSize={12} fontWeight={400} color={'#193F56'}>
        To start the search, sign the service agreement and pay a&nbsp;
        <strong>one-time $50 search fee</strong>. If no sitter profile is
        available, we’ll refund the fee.
      </Typography>
    ),
    src: APP_IMG.statSearching,
  },
  {
    title: '3. Get Matched',
    elementDescription: (
      <Typography fontSize={12} fontWeight={400} color={'#193F56'}>
        We’ll handpick a sitter based on her availability, suitability, and
        location. Once a match has been found, you’ll receive her profile, which
        we’ve pre-screened with ID verification, criminal background checks,
        interviews and other parents’ reviews.
      </Typography>
    ),
    src: APP_IMG.getMatched,
  },
  {
    title: '4. Arrange Trial',
    elementDescription: (
      <Typography fontSize={12} fontWeight={400} color={'#193F56'}>
        Try out your sitter at <strong>$35/hour (min. 3 hours)</strong>—payment
        is required before the trial. Use this time to assess the fit and
        interview her.
      </Typography>
    ),
    src: APP_IMG.arrangeTrial,
  },
  {
    title: '5. Select Package',
    elementDescription: (
      <Typography fontSize={12} fontWeight={400} color={'#193F56'}>
        If you're happy with the trial, select a package for more cost savings
        (valid for 6 months). Additional charges may apply for public holidays
        or extra children.
      </Typography>
    ),
    src: APP_IMG.selectPackages,
  },
  {
    title: '6. Sit Back',
    elementDescription: (
      <Typography fontSize={12} fontWeight={400} color={'#193F56'}>
        Your sitter will log her sessions, and hours will be deducted from your
        package. Sit back and relax! 😊
      </Typography>
    ),
    src: APP_IMG.sitBack,
  },
];

const HowItWorks = () => {
  const navigate = useNavigate();
  const { baseRequest } = useAppSelector((state) => state.overview);

  const [isNextScreen, setIsNextScreen] = useState<boolean>(true);

  const handleGetStart = async () => {
    navigate(APP_ROUTES.CREATE_REQUEST.to);
  };

  const handlePreviousAction = () => {
    setIsNextScreen(true);
  };

  const handleNextAction = () => {
    setIsNextScreen(false);
  };

  useEffect(() => {
    if (baseRequest) {
      navigate(APP_ROUTES.OVERVIEW.to);
    }
  }, [baseRequest, navigate]);

  const StepItem = ({ item, index }: { item: any; index: number }) => {
    return (
      <Fragment>
        <Stack spacing={{ xs: 0.25, md: 1.5 }}>
          <img src={item.src} alt={item.src} />
          <Stack spacing={1}>
            <Typography className="title-step">{item.title}</Typography>
            <Fragment>{item.elementDescription}</Fragment>
          </Stack>
        </Stack>
        {index !== 2 && (
          <ImageVector
            src={APP_IMG.brokenVector}
            alt={APP_IMG.brokenVector}
            isUseTransform={index === 1}
          />
        )}
      </Fragment>
    );
  };

  return (
    <Stack padding={2}>
      <Stack pb={{ xs: 4, md: 8 }}>
        <LabelTitle>Welcome to Aunty Concierge</LabelTitle>
        <LabelSubTitle>How it works</LabelSubTitle>
      </Stack>
      <BoxWrapper>
        {isNextScreen
          ? dataHowItWorks
              .filter((_, index) => index < 3)
              .map((item, index) => (
                <Fragment key={index}>
                  <StepItem item={item} index={index} />
                </Fragment>
              ))
          : dataHowItWorks
              .filter((_, index) => index >= 3)
              .map((item, index) => (
                <Fragment key={index}>
                  <StepItem item={item} index={index} />
                </Fragment>
              ))}
      </BoxWrapper>
      {isNextScreen ? (
        <StyledButtonWrapper isnextbutton>
          <Button variant="contained" onClick={handleNextAction}>
            Next
          </Button>
        </StyledButtonWrapper>
      ) : (
        <StyledButtonWrapper>
          <Button variant="outlined" onClick={handlePreviousAction}>
            Previous
          </Button>
          <Button variant="contained" onClick={handleGetStart}>
            Get Started
          </Button>
        </StyledButtonWrapper>
      )}
    </Stack>
  );
};

export default HowItWorks;
